import { supabase } from '../supabase';
import {
  TradeMessage,
  ProcessTradeParams,
  ProcessTradeStep2Params,
} from '../types/trade';
import { groupTradesByTransaction } from '../utils/tradeUtils';
import { sendTradeResponseEmail, extractTradeAssets } from './emailNotifications';

export async function getTradeMessages(
  leagueId: number,
  userId: number,
  options?: { page?: number; pageSize?: number }
) {
  try {
    const page = options?.page && options.page > 0 ? options.page : 1;
    const pageSize = options?.pageSize && options.pageSize > 0 ? options.pageSize : 2; // default to 2 items per page to avoid timeouts
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data, error } = await supabase
      .from('gettradeshistory_players_and_users')
      .select('*')
      .eq('leagueid', leagueId)
      .eq('is_accepted', false)
      .eq('is_refused', false)
      .or(`sender_user_id.eq.${userId},receiver_user_id.eq.${userId}`)
      .order('time_stamp', { ascending: false })
      .range(from, to);

    if (error) throw error;
    return { data: data as TradeMessage[], error: null };
  } catch (error: any) {
    console.error('Error fetching trade messages:', error);
    return { data: null, error };
  }
}

// New: fetch trades grouped by transaction (trades_id/coalesce) and paginate by groups
export async function getGroupedTradeMessages(
  leagueId: number,
  userId: number,
  options?: { page?: number; pageSize?: number }
) {
  try {
    const page = options?.page && options.page > 0 ? options.page : 1;
    const pageSize = options?.pageSize && options.pageSize > 0 ? options.pageSize : 2;

    // Fetch all relevant pending trade rows for the user/league
    const { data, error } = await supabase
      .from('gettradeshistory_players_and_users')
      .select('*')
      .eq('leagueid', leagueId)
      .eq('is_accepted', false)
      .eq('is_refused', false)
      .or(`sender_user_id.eq.${userId},receiver_user_id.eq.${userId}`)
      .order('time_stamp', { ascending: false });

    if (error) throw error;

    const allTrades = (data as TradeMessage[]) || [];

    // Group trades by transaction so items with the same trades_id stay together
    const grouped = groupTradesByTransaction(allTrades);

    const totalGroups = grouped.length;
    const from = (page - 1) * pageSize;
    const to = from + pageSize;

    const pageSlice = grouped.slice(from, to);

    return { data: pageSlice, totalGroups, error: null };
  } catch (error: any) {
    console.error('Error fetching grouped trade messages:', error);
    return { data: null, totalGroups: 0, error };
  }
}

export async function processTradeStep1({
  leagueId,
  thId,
  isAccepted,
  isRefused,
}: ProcessTradeParams) {
  try {
    // First, get the trade to find the group identifier
    const { data: currentTrade, error: currentTradeError } = await supabase
      .from('trades_history')
      .select('th_id, trades_id')
      .eq('th_id', thId)
      .single();

    if (currentTradeError) throw currentTradeError;

    // Determine the group identifier (trades_id or th_id if trades_id is null)
    const groupId = currentTrade.trades_id || currentTrade.th_id;

    // Get all trades that belong to this group
    const { data: tradeData, error: tradeError } = await supabase
      .from('trades_history')
      .select('th_id, trades_id')
      .or(`trades_id.eq.${groupId},th_id.eq.${groupId}`);

    if (tradeError) throw tradeError;

    // Get all trade IDs in this group
    const tradeIds = tradeData?.map(trade => trade.th_id) || [thId];

    console.log(`Processing trade group ${groupId} with trade IDs:`, tradeIds);

    // Update all trades in the group
    for (const currentThId of tradeIds) {
      // Use the jsonb wrapper overload by sending a single p_params object.
      const { error } = await supabase.rpc('accept_or_refuse_tradestep1', {
        p_params: {
          p_league_id: leagueId,
          p_th_id: currentThId,
          p_is_accepted: isAccepted,
          p_is_refused: isRefused,
        },
      });

      if (error) throw error;
    }

    // Email non-bloquant au proposeur (sender) — UNIQUEMENT si l'échange est refusé.
    // Si accepté, GroupedTradeCard envoie déjà sendTradeFinalizedEmail (avec assets) aux deux partis.
    if (isRefused) {
      try {
        const { data: tradeInfo } = await supabase
          .from('gettradeshistory_players_and_users')
          .select('sender_user_id, sender_username, receiver_username, leagueid')
          .eq('th_id', thId)
          .maybeSingle();

        if (tradeInfo?.sender_user_id) {
          // Requêter la vue complète pour avoir les noms des joueurs/picks
          const { data: fullTradeData } = await supabase
            .from('gettradeshistory_players_and_users')
            .select('*')
            .eq('leagueid', leagueId)
            .or(`th_id.eq.${groupId},trades_id.eq.${groupId}`);

          const [senderUserResult, leagueInfoResult] = await Promise.all([
            supabase
              .from('users')
              .select('email')
              .eq('user_id', tradeInfo.sender_user_id)
              .maybeSingle(),
            supabase
              .from('leagues')
              .select('name')
              .eq('league_id', leagueId)
              .maybeSingle(),
          ]);

          if (senderUserResult.data?.email) {
            const assets = extractTradeAssets(fullTradeData || []);
            await sendTradeResponseEmail({
              senderEmail: senderUserResult.data.email,
              senderUsername: tradeInfo.sender_username || '',
              receiverUsername: tradeInfo.receiver_username || '',
              isAccepted: false,
              leagueName: leagueInfoResult.data?.name || `Ligue #${leagueId}`,
              senderAssets: assets.senderAssets,
              receiverAssets: assets.receiverAssets,
            });
          }
        }
      } catch (emailErr) {
        console.error('Trade response email failed (non-blocking):', emailErr);
      }
    }

    return { data: tradeData, error: null };
  } catch (error: any) {
    console.error('Error processing trade step 1:', error);
    return { data: null, error };
  }
}

export async function processTradeStep2(params: ProcessTradeStep2Params) {
  try {
    const { data, error } = await supabase.rpc('accept_or_refuse_trade_step2', {
      p_league_id: params.leagueId,
      p_senderplayer_id: params.senderPlayerId,
      p_senderuser_id: params.senderUserId,
      p_receiverplayer_id: params.receiverPlayerId,
      p_receiveruser_id: params.receiverUserId,
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error processing trade step 2:', error);
    return { data: null, error };
  }
}

// Nouvelle fonction pour traiter les transferts de draft picks
export async function processDraftPickTrade({
  tradeSenderPickId,
  tradeReceiverPickId,
  senderUserId,
  receiverUserId,
  leagueId,
}: {
  tradeSenderPickId: number | null;
  tradeReceiverPickId: number | null;
  senderUserId: number;
  receiverUserId: number;
  leagueId: number;
}) {
  try {
    const { data, error } = await supabase.rpc('process_draft_pick_trade', {
      p_trade_sender_pick_id: tradeSenderPickId,
      p_trade_receiver_pick_id: tradeReceiverPickId,
      p_sender_user_id: senderUserId,
      p_receiver_user_id: receiverUserId,
      p_league_id: leagueId,
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error processing draft pick trade:', error);
    return { data: null, error };
  }
}

export async function processCompleteTrade({
  leagueId,
  trades,
}: {
  leagueId: number;
  trades: Array<{
    sender_user_id: number;
    receiver_user_id: number;
    sender_pick_id?: number | null;
    receiver_pick_id?: number | null;
    sender_player_id?: number | null;
    receiver_player_id?: number | null;
    sender_goalie_id?: number | null;
    receiver_goalie_id?: number | null;
  }>;
}) {
  try {
    const { data, error } = await supabase.rpc('process_complete_trade', {
      p_league_id: leagueId,
      p_trades: trades,
    });

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error processing complete trade:', error);
    return { data: null, error };
  }
}

export async function cancelTradeOffer({
  thId,
  leagueId,
}: {
  thId: number;
  leagueId: number;
}) {
  try {
    // First, get the trade to find the group identifier
    const { data: currentTrade, error: currentTradeError } = await supabase
      .from('trades_history')
      .select('th_id, trades_id')
      .eq('th_id', thId)
      .single();

    if (currentTradeError) throw currentTradeError;

    // Determine the group identifier (trades_id or th_id if trades_id is null)
    const groupId = currentTrade.trades_id || currentTrade.th_id;

    // Get all trades that belong to this group
    const { data: tradeData, error: tradeError } = await supabase
      .from('trades_history')
      .select('th_id')
      .or(`trades_id.eq.${groupId},th_id.eq.${groupId}`);

    if (tradeError) throw tradeError;

    // Get all trade IDs in this group
    const tradeIds = tradeData?.map(trade => trade.th_id) || [thId];

    console.log(`Cancelling trade group ${groupId} with trade IDs:`, tradeIds);

    // Update all trades in the group to set is_refused = true
    const { error: updateError } = await supabase
      .from('trades_history')
      .update({ is_refused: true })
      .in('th_id', tradeIds)
      .eq('leagueid', leagueId);

    if (updateError) throw updateError;

    return { data: tradeData, error: null };
  } catch (error: any) {
    console.error('Error cancelling trade offer:', error);
    return { data: null, error };
  }
}